<script>
  import { onMount, onDestroy } from 'svelte';
  import { fetchMatchFromCloudflareD1 } from '../../services/cloudflare-d1-sync.js';
  import { currentInnings, formatOvers, reqRate, currentOverBalls } from '../../scoring-logic.js';
  import { getMeta } from '../../store.js';

  export let matchCode = "";
  export let onBack = () => {};

  let match = null;
  let loading = true;
  let errorMsg = "";
  let pollInterval = null;
  let autoRefresh = true;
  let inputCode = matchCode || "";
  $: if (matchCode && inputCode !== matchCode) {
    inputCode = matchCode;
    loadMatch().then(startPolling);
  }

  async function loadMatch() {
    if (!inputCode) return;
    loading = true;
    errorMsg = "";
    const res = await fetchMatchFromCloudflareD1(inputCode);
    loading = false;

    if (res.ok && res.match) {
      match = res.match;
    } else {
      errorMsg = res.error || "Match not found";
    }
  }

  // clearInterval cannot cancel a fetch that is already in flight — its `then`
  // still lands after the component is gone and assigns to unmounted state.
  let unmounted = false;

  function startPolling() {
    stopPolling();
    if (autoRefresh) {
      pollInterval = setInterval(async () => {
        if (!inputCode || !match || unmounted) return;
        try {
          const res = await fetchMatchFromCloudflareD1(inputCode);
          if (unmounted) return; // resolved after the user navigated away
          if (res.ok && res.match) match = res.match;
        } catch (err) {
          console.warn('[Spectator] poll failed:', err);
        }
      }, 3000);
    }
  }

  function stopPolling() {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  }

  $: inn = match ? currentInnings(match) : null;
  $: battingTeam = match && inn ? (inn.battingTeam === 'A' ? match.teamA : match.teamB) : null;
  $: bowlingTeam = match && inn ? (inn.battingTeam === 'A' ? match.teamB : match.teamA) : null;
  $: batMeta = battingTeam ? getMeta(battingTeam.name) : { color: '#0a7d28', logo: null };
  $: bowlMeta = bowlingTeam ? getMeta(bowlingTeam.name) : { color: '#ef4444', logo: null };

  $: striker = inn ? inn.batsmen.find(b => b.name === inn.currentStriker) : null;
  $: nonStriker = inn ? inn.batsmen.find(b => b.name === inn.currentNonStriker) : null;
  $: bowler = inn ? inn.bowlers.find(b => b.name === inn.currentBowler) : null;

  $: overBalls = inn ? currentOverBalls(inn) : [];
  $: target = (match && match.innings.length === 2) ? match.innings[0].totalRuns + 1 : null;
  $: need = target && inn ? Math.max(0, target - inn.totalRuns) : 0;
  $: ballsLeft = target && inn ? Math.max(0, match.overs * 6 - inn.balls) : 0;

  onMount(() => {
    if (inputCode) {
      loadMatch().then(startPolling);
    }
  });

  onDestroy(() => {
    unmounted = true;
    stopPolling();
  });
</script>

<div class="spectator-container">
  <!-- Top Bar -->
  <div class="spectator-header">
    <button class="btn-back" on:click={onBack}>← Back</button>
    <div class="brand">
      <img src="/criccam_pro_logo.png" alt="Logo" class="logo" />
      <span class="title">LIVE MATCH SPECTATOR</span>
    </div>
    <div class="live-tag">
      <span class="dot"></span> LIVE
    </div>
  </div>

  <!-- Search / Join Code Bar -->
  {#if !matchCode}
    <div class="search-box">
      <input 
        type="text" 
        placeholder="Enter 6-char Match Code (e.g. ABCDEF)" 
        bind:value={inputCode} 
        maxlength="6"
        style="text-transform: uppercase;"
      />
      <button on:click={() => { loadMatch().then(startPolling); }}>Watch Live</button>
    </div>
  {/if}

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
      <p>Fetching Live Scorecard from Cloudflare Edge...</p>
    </div>
  {:else if errorMsg}
    <div class="error-state">
      <p>⚠️ {errorMsg}</p>
      <button on:click={loadMatch}>Retry</button>
    </div>
  {:else if match && inn}
    <!-- Main Live Banner -->
    <div class="score-banner">
      <div class="team-header">
        <div class="team-badge" style="background: {batMeta.color}">
          {#if batMeta.logo}<img src={batMeta.logo} alt="team" />{/if}
          <span>{battingTeam ? battingTeam.name : 'Batting Team'}</span>
        </div>
        <div class="vs">vs</div>
        <div class="team-badge" style="background: {bowlMeta.color}">
          {#if bowlMeta.logo}<img src={bowlMeta.logo} alt="team" />{/if}
          <span>{bowlingTeam ? bowlingTeam.name : 'Bowling Team'}</span>
        </div>
      </div>

      <div class="score-display">
        <div class="runs">{inn.totalRuns} <span class="wkts">/ {inn.wickets}</span></div>
        <div class="overs">({formatOvers(inn.balls)} / {match.overs} ov)</div>
      </div>

      <div class="match-rates">
        <span>CRR: {(inn.totalRuns / Math.max(1, inn.balls / 6)).toFixed(2)}</span>
        {#if target}
          <span class="need-text">Need {need} off {ballsLeft} balls (RRR: {reqRate(need, ballsLeft)})</span>
        {/if}
      </div>

      <!-- Current Over Ball Dots -->
      <div class="over-dots">
        <span class="label">This Over:</span>
        {#each overBalls as b, i_each (b.id || b.name || i_each)}
          <span class="dot-ball {b.isWicket ? 'wicket' : b.runs >= 4 ? 'boundary' : ''}">
            {b.isWicket ? 'W' : b.runs}
          </span>
        {/each}
      </div>
    </div>

    <!-- Active Players Card -->
    <div class="players-grid">
      <div class="player-card">
        <div class="card-title">🏏 Batsmen</div>
        {#if striker}
          <div class="player-row active">
            <span class="name">{striker.name} *</span>
            <span class="stats"><strong>{striker.runs}</strong> ({striker.balls})</span>
          </div>
        {/if}
        {#if nonStriker}
          <div class="player-row">
            <span class="name">{nonStriker.name}</span>
            <span class="stats"><strong>{nonStriker.runs}</strong> ({nonStriker.balls})</span>
          </div>
        {/if}
      </div>

      <div class="player-card">
        <div class="card-title">🎯 Bowler</div>
        {#if bowler}
          <div class="player-row">
            <span class="name">{bowler.name}</span>
            <span class="stats">{bowler.wickets}/{bowler.runs} ({formatOvers(bowler.balls)})</span>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .spectator-container {
    width: 100%;
    height: 100%;
    background: #070c18;
    color: #f1f5f9;
    padding: 16px;
    overflow-y: auto;
    font-family: 'Outfit', sans-serif;
  }
  .spectator-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
    margin-bottom: 16px;
  }
  .btn-back {
    background: rgba(255,255,255,0.1);
    border: none;
    color: white;
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .logo {
    width: 28px;
    height: 28px;
    border-radius: 6px;
  }
  .title {
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 0.5px;
    color: #fbbf24;
  }
  .live-tag {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .dot {
    width: 6px;
    height: 6px;
    background: #ef4444;
    border-radius: 50%;
  }
  .search-box {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }
  .search-box input {
    flex: 1;
    background: rgba(15, 23, 42, 0.8);
    border: 1px solid rgba(255,255,255,0.15);
    color: white;
    padding: 10px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 16px;
  }
  .search-box button {
    background: #dc2626;
    color: white;
    border: none;
    padding: 10px 16px;
    border-radius: 8px;
    font-weight: 700;
    cursor: pointer;
  }
  .score-banner {
    background: rgba(15, 23, 42, 0.9);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 12px;
    padding: 16px;
    text-align: center;
    margin-bottom: 16px;
  }
  .team-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 12px;
  }
  .team-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-weight: 700;
    font-size: 13px;
  }
  .vs {
    font-size: 12px;
    color: #94a3b8;
  }
  .score-display .runs {
    font-size: 42px;
    font-weight: 900;
    color: #ffffff;
  }
  .score-display .wkts {
    font-size: 24px;
    color: #ef4444;
  }
  .score-display .overs {
    font-size: 14px;
    color: #94a3b8;
    margin-top: -4px;
  }
  .match-rates {
    font-size: 13px;
    color: #fbbf24;
    margin-top: 8px;
  }
  .over-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 12px;
  }
  .dot-ball {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
  }
  .dot-ball.boundary { background: #fbbf24; color: black; }
  .dot-ball.wicket { background: #ef4444; color: white; }
  .players-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .player-card {
    background: rgba(15, 23, 42, 0.8);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px;
    padding: 12px;
  }
  .card-title {
    font-size: 12px;
    font-weight: 700;
    color: #94a3b8;
    margin-bottom: 8px;
  }
  .player-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    padding: 4px 0;
  }
  .player-row.active {
    color: #6ee591;
    font-weight: 700;
  }
  .loading-state, .error-state {
    text-align: center;
    padding: 40px;
    color: #94a3b8;
  }
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255,255,255,0.1);
    border-top-color: #dc2626;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 12px;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
</style>
